import { InventoryFee, InventoryItem, Venue } from 'types/model'
import { Booking } from 'types/model/booking'

/**
 *
 * subtotal = price * quantity
 * processing fee = automatic if enabled
 * custom fees = sum of all fees
 * tax & service charge = (subtotal + processing fee)*(tax% + service%)
 * grand total = subtotal + processing + fees + tax&service
 */

export function useBookingSubtotalNumbers(booking: Booking) {
  const isAdvanceDeposit = booking.payment === 'ADVANCE_DEPOSIT'
  const bookingItem = booking.items[0]

  const depositFromItems = booking.items.reduce(
    (acc, cur) => acc + (cur.inventory.depositAmount ?? 0),
    0
  )
  const deposit = !isAdvanceDeposit ? 0 : depositFromItems || booking.price || 0

  const subtotal = !isAdvanceDeposit
    ? booking.subtotal
    : booking.items.reduce((acc, cur) => acc + cur.price, 0)

  const taxableFees =
    bookingItem.inventory.fee?.fees?.filter((fee) => fee.taxable) ?? []
  const taxableFeeTotal = bookingItem.inventory.fee?.enable
    ? getFeeTotal(taxableFees, bookingItem.total)
    : 0

  const customFees = !isAdvanceDeposit
    ? booking.venueFeeTotal
    : getFeeTotal(
        bookingItem.inventory.fee?.enable
          ? (bookingItem.inventory.fee.fees ?? [])
          : [],
        bookingItem.total,
        booking.taxPercent
      )

  const processingFeeItem = booking.fees.find(
    (val) => val.feeType === 'PROCESSING'
  )

  const processingPercent =
    processingFeeItem?.type === 'PERCENT' ? processingFeeItem.amount : undefined

  const processing = !bookingItem.inventory.userPaysProcessing
    ? 0
    : !isAdvanceDeposit
      ? booking.processingFeeTotal
      : booking.processingFeeTotal
  const tax = !isAdvanceDeposit
    ? booking.tax
    : (bookingItem.inventory.tax ?? 0) * (subtotal + taxableFeeTotal)
  const tip = !isAdvanceDeposit
    ? booking.tip
    : (bookingItem.inventory.tip ?? 0) * subtotal
  const taxAndTip = !isAdvanceDeposit ? booking.taxAndTip : tax + tip

  const grandTotal = subtotal + processing + customFees + taxAndTip

  return {
    subtotal,
    deposit,
    tax,
    tip,
    processing,
    processingPercent,
    taxPercent: !isAdvanceDeposit
      ? booking.taxPercent
      : (bookingItem.inventory?.tax ?? 0),
    tipPercent: !isAdvanceDeposit
      ? booking.tipPercent
      : (bookingItem.inventory?.tip ?? 0),
    taxAndTip,
    grandTotal,
  }
}

export function useInventorySubtotalNumbers(
  inventory: InventoryItem,
  venue: Venue
) {
  const isAdvanceDeposit = inventory.paymentType === 'ADVANCE_DEPOSIT'
  const isFullPayment = inventory.paymentType === 'ADVANCE'
  const noPaymentToday = ['NONE', 'VENUE'].includes(inventory.paymentType)
  const subtotal = isAdvanceDeposit ? inventory.depositAmount : inventory.price

  const taxableFees = inventory.fee.fees?.filter((fee) => fee.taxable) ?? []
  const nontaxableFees = inventory.fee.fees?.filter((fee) => !fee.taxable) ?? []

  const taxableFeeTotal = inventory.fee.enable
    ? getFeeTotal(taxableFees, inventory.price)
    : 0
  const nontaxableFeeTotal = inventory.fee.enable
    ? getFeeTotal(nontaxableFees, inventory.price)
    : 0
  const customFeeTotal = taxableFeeTotal + nontaxableFeeTotal

  const tax = (inventory.tax ?? 0) * (inventory.price + taxableFeeTotal)
  const tip = (inventory.tip ?? 0) * inventory.price
  const taxAndTip = tax + tip

  const processingFee =
    inventory.type === 'TICKET'
      ? venue.ticketProcessingFee
      : venue.tableProcessingFee
  const processing =
    !inventory.userPaysProcessing || noPaymentToday
      ? 0
      : (subtotal + (isFullPayment ? customFeeTotal + taxAndTip : 0)) *
        processingFee

  const grandTotal =
    subtotal + processing + (isAdvanceDeposit ? 0 : customFeeTotal + taxAndTip)

  const dueOnArrival = isAdvanceDeposit
    ? inventory.price + customFeeTotal + processing + taxAndTip - grandTotal
    : undefined

  return {
    subtotal,
    dueOnArrival,
    deposit: inventory.depositAmount,
    tax,
    tip,
    processing,
    processingPercent: processingFee,
    taxPercent: inventory.tax,
    tipPercent: inventory.tip,
    taxAndTip,
    grandTotal,
  }
}

function getFeeTotal(fees: InventoryFee[], total = 0, tax = 0): number {
  return fees
    ?.filter((val) => val.feeType !== 'PROCESSING')
    .reduce((acc, cur) => acc + getFeeAmount(cur, total, tax), 0)
}

function getFeeAmount(fee: InventoryFee, total: number, tax = 0) {
  const amount = fee.type === 'FLAT' ? fee.amount : total * fee.amount
  const taxAmount = (fee.taxable ? tax : 0) * amount
  const totalAmount = amount + taxAmount

  return totalAmount
}
